import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Menu, X, ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const serviceAreas = {
  'St. Clair County': [{
    name: 'Port Huron',
    slug: 'port-huron'
  }, {
    name: 'Marysville',
    slug: 'marysville'
  }, {
    name: 'St. Clair',
    slug: 'st-clair'
  }, {
    name: 'Marine City',
    slug: 'marine-city'
  }, {
    name: 'New Baltimore',
    slug: 'new-baltimore'
  }, {
    name: 'Algonac',
    slug: 'algonac'
  }, {
    name: 'Ira Township',
    slug: 'ira-township'
  }, {
    name: 'China Township',
    slug: 'china-township'
  }, {
    name: 'Casco Township',
    slug: 'casco-township'
  }],
  'Macomb County': [{
    name: 'Harrison Township',
    slug: 'harrison-township'
  }, {
    name: 'Clinton Township',
    slug: 'clinton-township'
  }, {
    name: 'Chesterfield',
    slug: 'chesterfield'
  }, {
    name: 'Macomb Township',
    slug: 'macomb-township'
  }, {
    name: 'Sterling Heights',
    slug: 'sterling-heights'
  }, {
    name: 'Warren',
    slug: 'warren'
  }, {
    name: 'Roseville',
    slug: 'roseville'
  }, {
    name: 'Utica',
    slug: 'utica'
  }, {
    name: 'St. Clair Shores',
    slug: 'st-clair-shores'
  }],
  'Oakland County': [{
    name: 'Troy',
    slug: 'troy'
  }, {
    name: 'Rochester Hills',
    slug: 'rochester-hills'
  }, {
    name: 'Auburn Hills',
    slug: 'auburn-hills'
  }, {
    name: 'Pontiac',
    slug: 'pontiac'
  }, {
    name: 'Clarkston',
    slug: 'clarkston'
  }, {
    name: 'Lake Orion',
    slug: 'lake-orion'
  }, {
    name: 'Oxford',
    slug: 'oxford'
  }]
};
export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileExpanded(null);
  }, [location]);
  const navLinks = [{
    name: 'Home',
    path: '/'
  }, {
    name: 'Services',
    path: '/services',
    dropdown: [{
      name: 'Roofing & Repairs',
      path: '/services/roofing'
    }, {
      name: 'Gutter Services',
      path: '/services/gutters'
    }, {
      name: 'Siding',
      path: '/services/siding'
    }, {
      name: 'Windows',
      path: '/services/windows'
    }, {
      name: 'Painting',
      path: '/services/painting'
    }, {
      name: 'Shingle Selection',
      path: '/services/shingles'
    }]
  }, {
    name: 'Commercial',
    path: '/commercial',
    dropdown: [{
      name: 'Silicone Coatings',
      path: '/commercial/silicone'
    }, {
      name: 'TPO Roofing',
      path: '/commercial/tpo'
    }, {
      name: 'EPDM Roofing',
      path: '/commercial/epdm'
    }, {
      name: 'Commercial Roof Repair',
      path: '/commercial/repair'
    }]
  }, {
    name: 'Service Areas',
    path: '/areas',
    megaMenu: true
  }, {
    name: 'Storm Damage',
    path: '/storm-damage'
  }, {
    name: 'Gallery',
    path: '/gallery'
  }, {
    name: 'About',
    path: '/about'
  }, {
    name: 'Contact',
    path: '/contact'
  }];
  const toggleMobileSection = (name: string) => {
    setMobileExpanded(mobileExpanded === name ? null : name);
  };
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white ${isScrolled ? 'shadow-md py-2' : 'py-3'}`} data-id="element-214">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-215">
        <div className="flex justify-between items-center" data-id="element-216">
          {/* Logo — UNCHANGED */}
          <Link to="/" className="flex items-center gap-3 group" data-id="element-217">
            <div className="w-12 h-12 rounded-full bg-brand-blue flex flex-col items-center justify-center text-white shadow-sm group-hover:scale-105 transition-transform" data-id="element-218">
              <span className="font-heading font-bold text-xl leading-none" data-id="element-219">
                M
              </span>
              <span className="text-[8px] font-body tracking-wider" data-id="element-220">
                EST. 2009
              </span>
            </div>
            <div className="hidden sm:block" data-id="element-221">
              <div className="font-heading font-bold text-xl text-brand-gray-dark leading-tight" data-id="element-222">
                Moceri
              </div>
              <p className="font-body text-xs text-brand-gray tracking-wide uppercase" data-id="element-223">
                Residential Services
              </p>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden xl:flex items-center gap-5 font-body text-sm font-medium text-brand-gray-dark" data-id="element-224">
            {navLinks.map(link => <div key={link.name} className="relative group" data-id="element-225">
                <Link to={link.path} className={`flex items-center gap-1 hover:text-brand-blue transition-colors py-2 ${link.name === 'Storm Damage' ? 'text-red-600 hover:text-red-700 font-bold' : ''}`} data-id="element-226">
                  {link.name === 'Storm Damage' && <AlertTriangle size={14} data-id="element-227" />}
                  {link.name}
                  {(link.dropdown || link.megaMenu) && <ChevronDown size={14} className="group-hover:rotate-180 transition-transform duration-200" data-id="element-228" />}
                </Link>

                {/* Standard Dropdown */}
                {link.dropdown && !link.megaMenu && <div className="absolute top-full left-0 w-56 bg-white shadow-xl rounded-lg border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 translate-y-2 group-hover:translate-y-0 overflow-hidden z-50" data-id="element-229">
                    <div className="py-2" data-id="element-230">
                      {link.dropdown.map(item => <Link key={item.name} to={item.path} className="block px-4 py-2.5 text-sm text-brand-gray hover:bg-gray-50 hover:text-brand-blue transition-colors" data-id="element-231">
                          {item.name}
                        </Link>)}
                    </div>
                  </div>}

                {/* Mega Menu — Service Areas */}
                {link.megaMenu && <div className="absolute top-full -left-40 w-[680px] bg-white shadow-2xl rounded-xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 translate-y-2 group-hover:translate-y-0 z-50" data-id="element-232">
                    <div className="p-6" data-id="element-233">
                      <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100" data-id="element-234">
                        <h3 className="font-heading font-bold text-brand-gray-dark" data-id="element-235">
                          Our Service Areas
                        </h3>
                        <Link to="/areas" className="text-brand-blue text-xs font-semibold hover:text-brand-blue-dark flex items-center gap-1" data-id="element-236">
                          View All <ChevronRight size={12} data-id="element-237" />
                        </Link>
                      </div>
                      <div className="grid grid-cols-3 gap-6" data-id="element-238">
                        {Object.entries(serviceAreas).map(([county, cities]) => <div key={county} data-id="element-239">
                              <h4 className="font-heading font-bold text-brand-blue text-sm mb-3" data-id="element-240">
                                {county}
                              </h4>
                              <ul className="space-y-1.5" data-id="element-241">
                                {cities.map(city => <li key={city.slug} data-id="element-242">
                                    <Link to={`/area/${city.slug}`} className="text-sm text-brand-gray hover:text-brand-blue transition-colors block py-0.5" data-id="element-243">
                                      {city.name}
                                    </Link>
                                  </li>)}
                              </ul>
                            </div>)}
                      </div>
                    </div>
                  </div>}
              </div>)}
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden xl:flex items-center gap-4" data-id="element-244">
            <a href="tel:5862033701" className="flex items-center gap-2 text-brand-gray-dark hover:text-brand-blue transition-colors font-body font-semibold" data-id="element-245">
              <Phone size={18} className="text-brand-blue" data-id="element-246" />
              (586) 203-3701
            </a>
            <a href="#quote" className="bg-brand-blue text-white px-6 py-2.5 rounded-lg font-body font-semibold hover:bg-brand-blue-dark transition-colors shadow-sm" data-id="element-247">
              Get a Free Quote
            </a>
          </div>

          {/* Mobile Menu Toggle */}
          <button className="xl:hidden text-brand-gray-dark p-2" onClick={() => setMobileMenuOpen(true)} data-id="element-248">
            <Menu size={28} data-id="element-249" />
          </button>
        </div>
      </div>

      {/* Mobile Menu Drawer */}
      <AnimatePresence data-id="element-250">
        {mobileMenuOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/50 z-[60] xl:hidden" onClick={() => setMobileMenuOpen(false)} data-id="element-251" />
            <motion.div initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          type: 'tween',
          duration: 0.3
        }} className="fixed top-0 right-0 bottom-0 w-[85%] max-w-sm bg-white z-[70] shadow-2xl flex flex-col overflow-y-auto xl:hidden" data-id="element-252">
              {/* Mobile Header */}
              <div className="p-4 border-b border-gray-100 flex justify-between items-center" data-id="element-253">
                <div className="flex items-center gap-3" data-id="element-254">
                  <div className="w-10 h-10 rounded-full bg-brand-blue flex items-center justify-center text-white" data-id="element-255">
                    <span className="font-heading font-bold text-lg" data-id="element-256">M</span>
                  </div>
                  <span className="font-heading font-bold text-lg text-brand-gray-dark" data-id="element-257">
                    Moceri
                  </span>
                </div>
                <button onClick={() => setMobileMenuOpen(false)} className="p-2 text-brand-gray" data-id="element-258">
                  <X size={24} data-id="element-259" />
                </button>
              </div>

              {/* Mobile Nav */}
              <div className="p-4 flex flex-col gap-1 flex-1" data-id="element-260">
                {navLinks.map(link => <div key={link.name} className="border-b border-gray-50" data-id="element-261">
                    {link.dropdown || link.megaMenu ? <>
                        <button onClick={() => toggleMobileSection(link.name)} className="w-full flex items-center justify-between font-heading font-bold text-brand-gray-dark text-lg py-3" data-id="element-262">
                          <span className="flex items-center gap-2" data-id="element-263">
                            {link.name === 'Storm Damage' && <AlertTriangle size={16} className="text-red-600" data-id="element-264" />}
                            {link.name}
                          </span>
                          <ChevronDown size={18} className={`text-brand-gray transition-transform ${mobileExpanded === link.name ? 'rotate-180' : ''}`} data-id="element-265" />
                        </button>
                        <AnimatePresence data-id="element-266">
                          {mobileExpanded === link.name && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.2
                  }} className="overflow-hidden" data-id="element-267">
                              <div className="pl-4 pb-3 space-y-2" data-id="element-268">
                                <Link to={link.path} className="block text-brand-blue font-body text-sm font-semibold py-1" data-id="element-269">
                                  View All {link.name} →
                                </Link>
                                {link.dropdown && link.dropdown.map(item => <Link key={item.name} to={item.path} className="block text-brand-gray font-body text-sm py-1" data-id="element-270">
                                      {item.name}
                                    </Link>)}
                                {link.megaMenu && Object.entries(serviceAreas).map(([county, cities]) => <div key={county} className="mt-3" data-id="element-271">
                                        <h4 className="font-heading font-bold text-brand-blue text-xs uppercase tracking-wider mb-2" data-id="element-272">
                                          {county}
                                        </h4>
                                        <div className="grid grid-cols-2 gap-x-4 gap-y-1" data-id="element-273">
                                          {cities.map(city => <Link key={city.slug} to={`/area/${city.slug}`} className="text-brand-gray font-body text-sm py-0.5" data-id="element-274">
                                              {city.name}
                                            </Link>)}
                                        </div>
                                      </div>)}
                              </div>
                            </motion.div>}
                        </AnimatePresence>
                      </> : <Link to={link.path} className={`font-heading font-bold text-lg block py-3 flex items-center gap-2 ${link.name === 'Storm Damage' ? 'text-red-600' : 'text-brand-gray-dark'}`} data-id="element-275">
                        {link.name === 'Storm Damage' && <AlertTriangle size={16} data-id="element-276" />}
                        {link.name}
                      </Link>}
                  </div>)}

                {/* Extra mobile links */}
                <div className="border-b border-gray-50" data-id="element-277">
                  <Link to="/financing" className="font-heading font-bold text-brand-gray-dark text-lg block py-3" data-id="element-278">
                    Financing
                  </Link>
                </div>
                <div className="border-b border-gray-50" data-id="element-279">
                  <Link to="/reviews" className="font-heading font-bold text-brand-gray-dark text-lg block py-3" data-id="element-280">
                    Reviews
                  </Link>
                </div>
                <div className="border-b border-gray-50" data-id="element-281">
                  <Link to="/blog" className="font-heading font-bold text-brand-gray-dark text-lg block py-3" data-id="element-282">
                    Blog
                  </Link>
                </div>
              </div>

              {/* Mobile CTAs */}
              <div className="p-4 bg-gray-50 mt-auto" data-id="element-283">
                <a href="tel:5862033701" className="flex items-center justify-center gap-2 w-full bg-white border border-brand-blue text-brand-blue py-3 rounded-lg font-body font-semibold mb-3" data-id="element-284">
                  <Phone size={18} data-id="element-285" />
                  (586) 203-3701
                </a>
                <a href="#quote" onClick={() => setMobileMenuOpen(false)} className="flex items-center justify-center w-full bg-brand-blue text-white py-3 rounded-lg font-body font-semibold" data-id="element-286">
                  Get a Free Quote
                </a>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </header>;
}